// Pure Social helpers — the label vocabulary, the counter strip, the month
// grid and every schedule/restriction rule. No React, no DOM, so both
// `pages/Social.tsx` (desktop) and `components/socialMobile.tsx` (≤880px)
// import from here instead of from each other (mobile overhaul Phase D §10;
// same no-ESM-cycle split Phase B made with `lib/projectDetailShared.ts`).
// `pages/Social.tsx` re-exports everything it used to own, so existing
// imports and its test are unchanged.

import { weekOf } from '../../shared/dates';
import {
  SOCIAL_ASSET_CHANNELS,
  type Brand,
  type SocialAssetChannel,
  type SocialAssetStatus,
  type SocialAssetType,
  type SocialAssetWithProject,
} from '../../shared/types';
import { addDays, formatWeekdayDate } from './calendar';

/** The fixed type vocabulary, spelled as the mockups show it. */
export const TYPE_LABELS: Record<SocialAssetType, string> = {
  social_made: 'social-made',
  bts: 'bts',
  stills: 'stills',
  selects: 'selects',
  frames: 'frames',
  ptc: 'ptc',
};

export const STATUS_LABELS: Record<SocialAssetStatus, string> = {
  needs_prep: 'needs prep',
  ready: 'ready',
  scheduled: 'scheduled',
  used: 'used',
  restricted: 'restricted',
};

export const CHANNEL_LABELS: Record<SocialAssetChannel, string> = { ig: 'IG', linkedin: 'LinkedIn' };
/** Slot shorthand — a dual-channel post reads IG+LI, never two entries. */
export const CHANNEL_SHORT: Record<SocialAssetChannel, string> = { ig: 'IG', linkedin: 'LI' };

export const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const DOW = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** Filter-chip order per the mockup — ready leads, restricted sits last. */
export const STATUS_FILTER_ORDER: SocialAssetStatus[] = [
  'ready',
  'needs_prep',
  'scheduled',
  'used',
  'restricted',
];

/** An asset's channels in canonical IG-then-LinkedIn order, so a dual slot
    always renders the same way regardless of how it was stored. */
export function assetChannels(channels: string | null): SocialAssetChannel[] {
  if (!channels) return [];
  const stored = new Set(channels.split(',').map((c) => c.trim()));
  return SOCIAL_ASSET_CHANNELS.filter((c) => stored.has(c));
}

/** "IG", "LI" or "IG+LI" — one label for one slot, canonical order however
    the chips were clicked. */
export function channelLabel(channels: SocialAssetChannel[]): string {
  const ordered = SOCIAL_ASSET_CHANNELS.filter((c) => channels.includes(c));
  return ordered.length === 0 ? '—' : ordered.map((c) => CHANNEL_SHORT[c]).join('+');
}

export interface SocialCounters {
  planned: number;
  ready: number;
  needsPrep: number;
  restricted: number;
}

/** The persistent strip: planned next 14 days, ready & unused, needs prep,
    restricted. An effectively restricted asset counts only as restricted —
    it isn't postable, so it must never read as available work. */
export function socialCounters(assets: SocialAssetWithProject[], today: string): SocialCounters {
  const horizon = addDays(today, 13);
  const counters: SocialCounters = { planned: 0, ready: 0, needsPrep: 0, restricted: 0 };
  for (const asset of assets) {
    if (asset.effective_restricted) {
      counters.restricted += 1;
      continue;
    }
    if (
      asset.planned_date !== null
      && asset.planned_date >= today
      && asset.planned_date <= horizon
      && asset.status !== 'used'
    ) {
      counters.planned += 1;
    }
    if (asset.status === 'ready') counters.ready += 1;
    else if (asset.status === 'needs_prep') counters.needsPrep += 1;
  }
  return counters;
}

export interface CalendarDay {
  iso: string;
  day: number;
  inMonth: boolean;
  isToday: boolean;
  slots: SocialAssetWithProject[];
}

export interface CalendarWeek {
  start: string;
  end: string;
  days: CalendarDay[];
  /** Zero planned posts in a week that sits wholly inside the shown month —
      collapses into the amber gap bar. Part-weeks belong to the neighbouring
      month's grid, so they never nag here. */
  gap: boolean;
}

export function monthLabel(monthIso: string): string {
  const [year, month] = monthIso.split('-').map(Number);
  return `${MONTHS_FULL[month - 1]} ${year}`;
}

export function shiftMonth(monthIso: string, delta: number): string {
  const [year, month] = monthIso.split('-').map(Number);
  const total = year * 12 + (month - 1) + delta;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/** Mon-anchored grid for a YYYY-MM month. Restricted assets are dropped by
    the caller, so a week whose only post got blocked correctly falls back to
    a gap. */
export function calendarMonthWeeks(
  monthIso: string,
  assets: SocialAssetWithProject[],
  today: string,
  /** Every planned post, channel chip ignored — gaps are computed off this
      and only the slots come from `assets`. A week already holding a
      LinkedIn post is not free just because IG is selected, and calling it
      a gap invites double-booking it. Defaults to the shown assets, which
      is the same list whenever no channel is selected. */
  gapAssets: SocialAssetWithProject[] = assets,
): CalendarWeek[] {
  const monthEnd = addDays(`${shiftMonth(monthIso, 1)}-01`, -1);
  const weeks: CalendarWeek[] = [];
  let cursor = weekOf(`${monthIso}-01`).start;
  while (cursor <= monthEnd) {
    const weekEnd = addDays(cursor, 6);
    const days: CalendarDay[] = Array.from({ length: 7 }, (_, i) => {
      const iso = addDays(cursor, i);
      return {
        iso,
        day: Number(iso.slice(8, 10)),
        inMonth: iso.slice(0, 7) === monthIso,
        isToday: iso === today,
        slots: assets.filter((a) => a.planned_date === iso),
      };
    });
    const planned = gapAssets.some(
      (a) => a.planned_date !== null && a.planned_date >= cursor && a.planned_date <= weekEnd,
    );
    weeks.push({
      start: cursor,
      end: weekEnd,
      days,
      gap: !planned && days.every((d) => d.inMonth),
    });
    cursor = addDays(cursor, 7);
  }
  return weeks;
}

// ── Mobile calendar (Phase D §10.1 B) ────────────────────────────────────

/** Which dot a slot draws in a `.social-mgrid` cell: teal for HSP, gold for
    SS, grey once posted, outlined while it still needs prep. Status is one
    enum, so used and prep can never both apply — the order below is the
    whole rule. */
export type SlotDot = 'hsp' | 'ss' | 'used' | 'prep';

export function slotDot(asset: SocialAssetWithProject): SlotDot {
  if (asset.status === 'used') return 'used';
  if (asset.status === 'needs_prep') return 'prep';
  return asset.brand === 'ss' ? 'ss' : 'hsp';
}

/** One planned post on one day. A dual-channel asset is ONE slot reading
    "IG+LI" — never two rows, never two dots (the desktop calendar reads the
    same way, and two entries would imply two posts). */
export interface DaySlot {
  id: string;
  asset: SocialAssetWithProject;
  channels: SocialAssetChannel[];
  /** "IG" · "LI" · "IG+LI", or "—" when the slot lost its channels. */
  label: string;
  dot: SlotDot;
  used: boolean;
}

/** The slots planned for one ISO day, in the order the assets arrive (the
    API sorts them), turned into what the grid and the day list both render. */
export function daySlots(assets: SocialAssetWithProject[], iso: string): DaySlot[] {
  return assets
    .filter((a) => a.planned_date === iso)
    .map((asset) => {
      const channels = assetChannels(asset.channels);
      return {
        id: asset.id,
        asset,
        channels,
        label: channelLabel(channels),
        dot: slotDot(asset),
        used: asset.status === 'used',
      };
    });
}

/** The day the calendar opens on: today when the shown month contains it,
    otherwise the first day of that month that already holds a slot. A month
    with neither gets no day list at all — the grid and the tray stand alone
    until a day is tapped. */
export function defaultSelectedDay(weeks: CalendarWeek[], today: string): string | null {
  const days = weeks.flatMap((w) => w.days).filter((d) => d.inMonth);
  if (days.some((d) => d.iso === today)) return today;
  return days.find((d) => d.slots.length > 0)?.iso ?? null;
}

// ── Scheduling ───────────────────────────────────────────────────────────

export interface ScheduleDraft {
  asset: SocialAssetWithProject;
  date: string;
  channels: SocialAssetChannel[];
}

/** The confirm button always restates the full choice, per the mockup. */
export function scheduleConfirmLabel(draft: ScheduleDraft): string {
  return `Schedule · ${channelLabel(draft.channels)} · ${formatWeekdayDate(draft.date)}`;
}

/** The mobile schedule sheet's footer primary — same restatement, but with
    something to say while the button is still disabled (no channel picked,
    which is the one state the desktop popover can also reach). */
export function scheduleSheetLabel(channels: SocialAssetChannel[], date: string): string {
  if (channels.length === 0) return 'Pick a channel';
  return `Schedule · ${channelLabel(channels)} · ${formatWeekdayDate(date)}`;
}

/** PATCH body for a schedule / move-date / edit-channels confirm. One slot,
    one or both channels, stored in canonical order. */
export function schedulePatch(draft: ScheduleDraft): {
  status: 'scheduled';
  planned_date: string;
  channels: string;
} {
  return {
    status: 'scheduled',
    planned_date: draft.date,
    channels: SOCIAL_ASSET_CHANNELS.filter((c) => draft.channels.includes(c)).join(','),
  };
}

/** Repurpose: a fresh asset pre-filled from the used one, back at needs prep.
    The original stays in the library so the history stays honest. */
export function repurposeDraft(asset: SocialAssetWithProject): Record<string, unknown> {
  return {
    project_id: asset.project_id,
    type: asset.type,
    label: `${asset.label} (repurposed)`,
    url: asset.url,
    note: asset.note,
    brand: asset.brand,
    status: 'needs_prep',
  };
}

/** Repurpose fires a POST, so a second click while the first is in flight
    leaves a duplicate copy behind. One at a time; the caller reveals what
    comes back (the copy lands at needs prep, so under the used chip that
    fired the click it would otherwise be invisible). Returns null when the
    click was blocked or the POST failed — nothing changed either way. */
export async function runRepurpose(
  asset: SocialAssetWithProject,
  inFlight: { current: boolean },
  post: (json: Record<string, unknown>) => Promise<{ asset: SocialAssetWithProject }>,
): Promise<SocialAssetWithProject | null> {
  if (inFlight.current) return null;
  inFlight.current = true;
  try {
    return (await post(repurposeDraft(asset))).asset;
  } catch {
    return null;
  } finally {
    inFlight.current = false;
  }
}

/** Where an effective restriction comes from. The project flag wins: it
    blocks every asset under the project and can only be cleared there. */
export function restrictionSource(
  asset: SocialAssetWithProject,
): 'project' | 'asset' | null {
  if (asset.project_social_restricted === 1) return 'project';
  return asset.status === 'restricted' ? 'asset' : null;
}

export function toggleChannel(
  channels: SocialAssetChannel[],
  channel: SocialAssetChannel,
): SocialAssetChannel[] {
  return channels.includes(channel)
    ? channels.filter((c) => c !== channel)
    : [...channels, channel];
}

export function shortUrl(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

/** Only a plain http(s) address is safe to hand a click. Rows imported or
    typed before the API validated urls can hold anything — javascript:,
    data:, a bare filename — so those render as text instead of a link. */
export function safeUrl(url: string | null): string | null {
  const trimmed = url?.trim() ?? '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
}

// ── Library filtering ────────────────────────────────────────────────────

export interface SocialFilters {
  status: SocialAssetStatus | null;
  brand: Brand | null;
  type: SocialAssetType | null;
}

/** Status filtering runs off the effective flag, so a project-flagged asset
    lists under "restricted" even though its own row still says ready. */
export function filterAssets(
  assets: SocialAssetWithProject[],
  filters: SocialFilters,
): SocialAssetWithProject[] {
  return assets.filter((asset) => {
    if (filters.status === 'restricted' && !asset.effective_restricted) return false;
    if (filters.status !== null && filters.status !== 'restricted') {
      if (asset.effective_restricted || asset.status !== filters.status) return false;
    }
    if (filters.brand !== null && asset.brand !== filters.brand) return false;
    if (filters.type !== null && asset.type !== filters.type) return false;
    return true;
  });
}

/** Filters relaxed just enough to show a freshly saved asset. The library
    defaults to the ready filter, so adding a needs-prep asset would
    otherwise close the modal onto no visible change. Only the dimension
    that hides the row moves — a mismatched status chip switches to the
    asset's own, a mismatched brand/type chip clears. */
export function filtersRevealing(
  asset: SocialAssetWithProject,
  filters: SocialFilters,
): SocialFilters {
  if (filterAssets([asset], filters).length > 0) return filters;
  const status = asset.effective_restricted ? 'restricted' : asset.status;
  return {
    status: filters.status === null || filters.status === status ? filters.status : status,
    brand: filters.brand === null || filters.brand === asset.brand ? filters.brand : null,
    type: filters.type === null || filters.type === asset.type ? filters.type : null,
  };
}

// ── Shared action surface ────────────────────────────────────────────────

/** Every action a row, slot or tray card can fire. Grouped so the two body
    components stay renderable from a test with one noop object. */
export interface SocialActions {
  onAdd: () => void;
  onOpenAsset: (id: string) => void;
  onEdit: (asset: SocialAssetWithProject) => void;
  onDelete: (asset: SocialAssetWithProject) => void;
  onRestrict: (asset: SocialAssetWithProject) => void;
  onUnrestrict: (asset: SocialAssetWithProject) => void;
  onMarkReady: (asset: SocialAssetWithProject) => void;
  onRepurpose: (asset: SocialAssetWithProject) => void;
  onStartSchedule: (asset: SocialAssetWithProject, date?: string) => void;
  onUnschedule: (asset: SocialAssetWithProject) => void;
  onMarkUsed: (asset: SocialAssetWithProject) => void;
}

export type ChannelFilter = 'all' | SocialAssetChannel;
